import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { UiContainer, UiNodeAnchorAttributes, UiNodeAttributes, UiNodeInputAttributes } from '../../../apis/ory';

// The generated `UiNodeAttributes` union gives every member the full five-value `node_type` enum, so
// TypeScript cannot discriminate on it by itself. These two predicates carry the same check the Ory
// SDK's own `isUiNode*Attributes` helpers make, and they are what lets the template read `type`,
// `name`, `value`, `disabled`, `required` and `href` off the narrowed member.
function isInputAttributes(attributes: UiNodeAttributes): attributes is UiNodeInputAttributes {
  return attributes.node_type === 'input';
}

function isAnchorAttributes(attributes: UiNodeAttributes): attributes is UiNodeAnchorAttributes {
  return attributes.node_type === 'a';
}

@Component({
  selector: 'nggc-generated-form',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <!-- {{ form | async | json }} -->
    @if (form | async; as form) {
      <!-- [action]="form.ui.action" [method]="form.ui.method" -->
      <form (ngSubmit)="formSubmit.emit(this.genFormVC?.value)" #genForm="ngForm">
        <!-- Each branch tests the attributes member instead of node.type. The schema defines the two as
             mirrors of each other, and only the member carries type, name, value, disabled and href. -->
        <!-- Tracking by $index because no field is both present on every node and unique: UiNode has no id,
             input attributes have none either, and their name repeats across grouped submit buttons such as
             one per OIDC provider. Node order is fixed for a given flow, so position is the stable identity. -->
        @for (node of form.ui.nodes; track $index) {
          @if (isInputAttributes(node.attributes) && node.attributes.type === 'hidden') {
            <!-- [(ngModel)]="node.attributes" -->
            <input
              [disabled]="node.attributes.disabled"
              [name]="node.attributes.name"
              [required]="node.attributes.required ?? false"
              [type]="node.attributes.type"
              [(ngModel)]="node.attributes.value"
            />
            <!-- [value]="node.attributes.value" -->
          } @else if (isInputAttributes(node.attributes) && node.attributes.type === 'submit') {
            @if (node.group === 'profile') {
              <div class="mb-3">
                <input
                  class="form-control"
                  [disabled]="false"
                  [name]="'password'"
                  [required]="true"
                  [(ngModel)]="node.messages[0]"
                  [type]="'password'"
                  [placeholder]="'Password'"
                />
              </div>
            }
            <div class="mb-3">
              <button
                class="btn btn-info"
                [disabled]="node.attributes.disabled"
                [name]="node.attributes.name"
                [type]="node.attributes.type"
              >
                {{ node.meta.label?.text }}
              </button>
            </div>
          } @else if (!(isInputAttributes(node.attributes) && node.attributes.name === 'traits.image')) {
            <div class="mb-3">
              @if (isAnchorAttributes(node.attributes)) {
                <a class="btn btn-info" [href]="node.attributes.href">
                  {{ node.meta.label?.text }}
                </a>
              } @else if (isInputAttributes(node.attributes)) {
                <!-- [(ngModel)]="node.attributes" -->
                <input
                  class="form-control"
                  [disabled]="node.attributes.disabled"
                  [name]="node.attributes.name"
                  [required]="node.attributes.required ?? false"
                  [type]="node.attributes.type"
                  [(ngModel)]="node.attributes.value"
                  [placeholder]="node.meta.label?.text"
                />
                <!-- [value]="node.attributes.value ? node.attributes.value : null" -->
              }
              @for (message of node.messages; track message.id) {
                <span class="text-danger small" [ngClass]="{ 'text-danger': message.type === 'error' }">
                  {{ message.text }}
                </span>
              }
            </div>
          }
        }
        @for (message of form.ui.messages; track message.id) {
          <div
            class="alert mt-4"
            role="alert"
            [ngClass]="{
              'alert-warning': message.type === 'error',
              'alert-success': message.type === 'success',
              'alert-info': message.type === 'info',
            }"
          >
            {{ message.text }}
          </div>
        }
      </form>
    }
  `,
})
export class GeneratedFormComponent {
  @ViewChild('genForm') public genFormVC?: NgForm;
  // Every Ory flow (login, registration, settings, recovery, verification) carries its form in a
  // UiContainer, so the structural type accepts all of them without importing one flow in particular.
  @Input() public form: Observable<{ ui: UiContainer }> = of();
  @Output() public readonly formSubmit = new EventEmitter<Record<string, unknown> | undefined>();
  protected readonly isInputAttributes = isInputAttributes;
  protected readonly isAnchorAttributes = isAnchorAttributes;
}
